import { useState, useEffect, useCallback, useRef } from "react";
import { useGlobalFilters } from "../../../../stores/GlobalFilterContext/GlobalFilterContext";
import { useGlobalAPI } from "../../../../hooks/useGlobalAPI";
import { useOcelMetadata } from "../../../../stores/OcelContext";
import { makeRequest } from "../../../../services/api/client";

const STORAGE_KEY = (activeFile) => `overdueValueTimeline_v2_${activeFile || "default"}`;

function loadFromStorage(activeFile) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(activeFile));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveToStorage(activeFile, state) {
  try {
    localStorage.setItem(STORAGE_KEY(activeFile), JSON.stringify(state));
  } catch {
    // storage full — silently ignore
  }
}

export function useOverdueValueTimeline(activeFile) {
  const { filterState } = useGlobalFilters();
  const api = useGlobalAPI(activeFile);
  const metadata = useOcelMetadata(activeFile);

  // ISO date string of the dataset's last event (e.g. "2026-01-15") — used as period_end
  // so the X-axis never extends past the actual dataset coverage.
  const datasetEnd = metadata?.date_range?.end
    ? metadata.date_range.end.slice(0, 10)
    : null;

  // ── Settings state (persisted) ──────────────────────────────────────────────
  const [componentTitle, setComponentTitle] = useState("");
  const [objectType, setObjectType] = useState("");
  const [targetDateAttribute, setTargetDateAttribute] = useState("");
  const [completionSourceType, setCompletionSourceType] = useState("object_attribute");
  const [completionSourceValue, setCompletionSourceValue] = useState([]);
  const [valueAttribute, setValueAttribute] = useState("");

  // ── Date scope state (persisted) ────────────────────────────────────────────
  const [viewMode, setViewMode] = useState("granularity"); // "granularity" | "daterange"
  const [granularity, setGranularity] = useState("day");
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");

  // ── Attribute options ────────────────────────────────────────────────────────
  const [allObjectAttributes, setAllObjectAttributes] = useState([]);
  const [numericObjectAttributes, setNumericObjectAttributes] = useState([]);
  const [attrsLoading, setAttrsLoading] = useState(false);

  // ── Data state ───────────────────────────────────────────────────────────────
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isFirstFilterRender = useRef(true);

  // ── Restore from localStorage on mount ──────────────────────────────────────
  useEffect(() => {
    const saved = loadFromStorage(activeFile);
    if (saved) {
      if (saved.componentTitle !== undefined) setComponentTitle(saved.componentTitle);
      if (saved.objectType !== undefined) setObjectType(saved.objectType);
      if (saved.targetDateAttribute !== undefined) setTargetDateAttribute(saved.targetDateAttribute);
      if (saved.completionSourceType !== undefined) setCompletionSourceType(saved.completionSourceType);
      if (saved.completionSourceValue !== undefined) {
        setCompletionSourceValue(
          Array.isArray(saved.completionSourceValue)
            ? saved.completionSourceValue
            : saved.completionSourceValue ? [saved.completionSourceValue] : []
        );
      }
      if (saved.valueAttribute !== undefined) setValueAttribute(saved.valueAttribute);
      if (saved.viewMode !== undefined) setViewMode(saved.viewMode);
      if (saved.granularity !== undefined) setGranularity(saved.granularity);
      if (saved.dateRangeStart !== undefined) setDateRangeStart(saved.dateRangeStart);
      if (saved.dateRangeEnd !== undefined) setDateRangeEnd(saved.dateRangeEnd);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist to localStorage on every state change ───────────────────────────
  useEffect(() => {
    saveToStorage(activeFile, {
      componentTitle,
      objectType,
      targetDateAttribute,
      completionSourceType,
      completionSourceValue,
      valueAttribute,
      viewMode,
      granularity,
      dateRangeStart,
      dateRangeEnd,
    });
  }, [activeFile, componentTitle, objectType, targetDateAttribute, completionSourceType,
      completionSourceValue, valueAttribute, viewMode, granularity,
      dateRangeStart, dateRangeEnd]);

  // ── Fetch object attributes when object type changes ─────────────────────────
  const fetchAttributes = useCallback(async (type) => {
    if (!activeFile || !type) {
      setAllObjectAttributes([]);
      setNumericObjectAttributes([]);
      return;
    }
    setAttrsLoading(true);
    try {
      const data = await makeRequest("/ocel/object-type-attributes", {
        params: { file_type: activeFile, object_type: type },
      }, "object type attributes");
      setAllObjectAttributes(data?.all_attributes || data?.object_attribute_names || []);
      setNumericObjectAttributes(data?.numeric_attributes || []);
    } catch {
      setAllObjectAttributes([]);
      setNumericObjectAttributes([]);
    } finally {
      setAttrsLoading(false);
    }
  }, [activeFile]);

  useEffect(() => {
    fetchAttributes(objectType);
  }, [objectType, fetchAttributes]);

  // ── Configuration completeness ───────────────────────────────────────────────
  const hasConfig = !!(
    objectType &&
    targetDateAttribute &&
    completionSourceType &&
    completionSourceValue.length > 0 &&
    valueAttribute
  );

  // ── Data fetching — period_end is clamped to dataset end so X-axis never exceeds real data ──
  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a filter change) must not overwrite a
  // newer one.
  const fetchSeqRef = useRef(0);
  const fetchData = useCallback(async () => {
    if (!hasConfig) return;
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await api.post(
        "/kpi/overdue-value-timeline",
        {
          object_type: objectType,
          target_date_attribute: targetDateAttribute,
          completion_source_type: completionSourceType,
          completion_source_value: completionSourceValue,
          value_attribute: valueAttribute,
          ...(datasetEnd && { period_end: datasetEnd }),
        },
        {},
        "Overdue Value Timeline"
      );
      if (seq !== fetchSeqRef.current) return;
      setChartData(result);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError(err?.message || "Fehler beim Laden der Daten");
      setChartData(null);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [api, hasConfig, objectType, targetDateAttribute, completionSourceType,
      completionSourceValue, valueAttribute, datasetEnd]);

  // Re-fetch when global filters change (skip mount)
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    if (hasConfig) fetchData();
  }, [filterState]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // settings
    componentTitle, setComponentTitle,
    objectType, setObjectType,
    targetDateAttribute, setTargetDateAttribute,
    completionSourceType, setCompletionSourceType,
    completionSourceValue, setCompletionSourceValue,
    valueAttribute, setValueAttribute,
    // date scope
    viewMode, setViewMode,
    granularity, setGranularity,
    dateRangeStart, setDateRangeStart,
    dateRangeEnd, setDateRangeEnd,
    // attribute options
    fetchAttributes,
    allObjectAttributes,
    numericObjectAttributes,
    attrsLoading,
    // data
    chartData,
    loading,
    error,
    hasConfig,
    fetchData,
    datasetEnd,
  };
}
